import { Link, useMatches, useParams } from "@tanstack/react-router";
import type { ReactElement, ReactNode } from "react";

import { useListBrands } from "../../api/generated/endpoints/teams/teams";
import { type NavTarget, sectionOf } from "./nav";

/** What the shell puts on a nav link: its look, its words and the `data-*` it is styled by. */
export interface RootProps {
  className: string | undefined;
  children: ReactNode;
  onClick?: (() => void) | undefined;
  "aria-label"?: string | undefined;
  "aria-current"?: "page" | undefined;
  [attribute: `data-${string}`]: boolean | undefined;
}

// The shell marks the active item from the route (nav.ts), so a link is active by TanStack's own
// rule only on its exact location, and the two never disagree.
const EXACT = { exact: true, includeSearch: true };

/**
 * A link to a nav target in this team, keeping TanStack's typed `to` and `params`. `undefined`
 * when there is nowhere to go: the item is coming soon, or it is the brand's and there is none.
 */
export function targetRoot(
  target: NavTarget,
  teamId: string,
  brandId: string | undefined,
): ((props: RootProps) => ReactElement) | undefined {
  switch (target.scope) {
    case "team":
      return (props) => (
        <Link {...props} to={target.to} params={{ teamId }} activeOptions={EXACT} />
      );
    case "brand": {
      if (brandId === undefined) return undefined;
      const search = target.medium === undefined ? {} : { medium: target.medium };
      return (props) => (
        <Link
          {...props}
          to={target.to}
          params={{ teamId, brandId }}
          search={search}
          activeOptions={EXACT}
        />
      );
    }
    case "staff":
      return (props) => <Link {...props} to={target.to} activeOptions={EXACT} />;
    case "soon":
      return undefined;
  }
}

/** The nav section of the page on screen, from the deepest matched route. */
export function useCurrentSection() {
  return useMatches({
    select: (matches) => {
      const leaf = matches.at(-1);
      return leaf === undefined ? undefined : sectionOf(leaf.routeId, leaf.search);
    },
  });
}

/**
 * The team's brands, the one shown as selected (the URL's, else the team's first), and the one
 * brand-scoped links go to. The URL stays the only place a brand is chosen (F3).
 */
export function useNavBrands(teamId: string) {
  const urlBrandId = useParams({ strict: false, select: (params) => params.brandId });
  const brands = useListBrands(teamId);
  const list = brands.data;
  const current =
    list?.find((brand) => brand.id.toLowerCase() === urlBrandId?.toLowerCase()) ?? list?.[0];
  return {
    brands: list,
    current,
    linkBrandId: urlBrandId ?? list?.[0]?.id,
    failed: brands.isError,
  };
}
